import { Plane, Raycaster, Vector2, Vector3, type Camera, type Object3D } from 'three';

export interface DragHooks {
  canvas: HTMLCanvasElement;
  camera: Camera;
  cardMeshes(): Object3D[];
  getDragStack(picked: Object3D): Object3D[];
  onDrop(lead: Object3D, x: number, z: number): boolean;
  onClick(picked: Object3D): void;
  onDoubleClick?(picked: Object3D): void;
  onCancel(stack: Object3D[]): void;
  onMove?(): void;
  onPickStart?(): void;
  liftY?: number;
  dragThresholdPx?: number;
  doubleClickMs?: number;
}

function findCardGroup(obj: Object3D): Object3D | null {
  let cur: Object3D | null = obj;
  while (cur && !cur.userData.cardId) cur = cur.parent;
  if (!cur) return null;
  while (cur.parent && cur.parent.userData.cardId === cur.userData.cardId) cur = cur.parent;
  return cur;
}

export function attachDrag(opts: DragHooks): () => void {
  const liftY = opts.liftY ?? 0.35;
  const threshold = opts.dragThresholdPx ?? 6;
  const doubleClickMs = opts.doubleClickMs ?? 300;
  let lastClickTime = 0;
  let lastClickGroup: Object3D | null = null;

  const ray = new Raycaster();
  const ndc = new Vector2();
  const dragPlane = new Plane(new Vector3(0, 1, 0), -liftY);
  const hit = new Vector3();
  const offset = new Vector3();
  const origin = new Map<Object3D, Vector3>();

  let stack: Object3D[] | null = null;
  let lead: Object3D | null = null;
  let pointerStart: { x: number; y: number } | null = null;
  let dragging = false;
  let activePointerId: number | null = null;

  function setNdc(e: PointerEvent) {
    const rect = opts.canvas.getBoundingClientRect();
    ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
  }

  function pick(): Object3D | null {
    ray.setFromCamera(ndc, opts.camera);
    const hits = ray.intersectObjects(opts.cardMeshes(), true);
    if (hits.length === 0) return null;
    return findCardGroup(hits[0]!.object);
  }

  function onPointerDown(e: PointerEvent) {
    if (activePointerId !== null) return;
    setNdc(e);
    const picked = pick();
    if (!picked) return;
    opts.onPickStart?.();
    activePointerId = e.pointerId;
    pointerStart = { x: e.clientX, y: e.clientY };
    dragging = false;
    lead = picked;
    stack = opts.getDragStack(picked);
    origin.clear();
    for (const m of stack) origin.set(m, m.position.clone());
    opts.canvas.setPointerCapture?.(e.pointerId);
    e.preventDefault();
  }

  function onPointerMove(e: PointerEvent) {
    if (e.pointerId !== activePointerId || !lead || !stack || !pointerStart) return;
    const dx = e.clientX - pointerStart.x;
    const dy = e.clientY - pointerStart.y;
    if (!dragging) {
      if (Math.hypot(dx, dy) < threshold) return;
      if (stack.length === 0) return;
      dragging = true;
      setNdc(e);
      ray.setFromCamera(ndc, opts.camera);
      ray.ray.intersectPlane(dragPlane, hit);
      offset.copy(hit).sub(origin.get(lead)!);
      offset.y = 0;
    }
    setNdc(e);
    ray.setFromCamera(ndc, opts.camera);
    if (!ray.ray.intersectPlane(dragPlane, hit)) return;
    const leadOrigin = origin.get(lead)!;
    const tx = hit.x - offset.x;
    const tz = hit.z - offset.z;
    stack.forEach((m, i) => {
      const dz = origin.get(m)!.z - leadOrigin.z;
      m.position.set(tx, liftY + i * 0.002, tz + dz);
    });
    opts.onMove?.();
  }

  function reset() {
    stack = null;
    lead = null;
    pointerStart = null;
    dragging = false;
    activePointerId = null;
  }

  function onPointerUp(e: PointerEvent) {
    if (e.pointerId !== activePointerId || !lead) return;
    opts.canvas.releasePointerCapture?.(e.pointerId);
    if (!dragging) {
      const now = performance.now();
      const isDouble = lead === lastClickGroup && now - lastClickTime < doubleClickMs;
      if (isDouble && opts.onDoubleClick) {
        opts.onDoubleClick(lead);
        lastClickTime = 0;
        lastClickGroup = null;
      } else {
        opts.onClick(lead);
        lastClickTime = now;
        lastClickGroup = lead;
      }
      reset();
      return;
    }
    if (!stack) {
      reset();
      return;
    }
    const ok = opts.onDrop(lead, lead.position.x, lead.position.z);
    if (!ok) opts.onCancel(stack);
    reset();
  }

  function onPointerCancel(e: PointerEvent) {
    if (e.pointerId !== activePointerId) return;
    if (stack) opts.onCancel(stack);
    reset();
  }

  opts.canvas.addEventListener('pointerdown', onPointerDown);
  opts.canvas.addEventListener('pointermove', onPointerMove);
  opts.canvas.addEventListener('pointerup', onPointerUp);
  opts.canvas.addEventListener('pointercancel', onPointerCancel);

  return () => {
    opts.canvas.removeEventListener('pointerdown', onPointerDown);
    opts.canvas.removeEventListener('pointermove', onPointerMove);
    opts.canvas.removeEventListener('pointerup', onPointerUp);
    opts.canvas.removeEventListener('pointercancel', onPointerCancel);
  };
}
